import { faArrowDown, faSearch } from '@fortawesome/pro-regular-svg-icons'
import { faCaretRight } from '@fortawesome/pro-solid-svg-icons'
import {
  Box,
  Button,
  Flex,
  LoadingOverlay,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { useState } from 'react'

import { useTranslation } from '@flecto/translations'
import { type CoreItem, type CoreBundleItemPayload } from '@flecto/types'
import { Badge, Icon, Modal, Result } from '@flecto/ui'

import { useBundleItemsFilter } from '../../hooks/useBundleItemsFilter'
import { BundleModalItemRow } from './BundleModalItemRow'

export type ModalBundleItemsSelectionProps = {
  handleItemsUpdate?: (
    items: Array<
      CoreItem & {
        quantity: number
      }
    >
  ) => void
  itemsInBundle?: Array<CoreBundleItemPayload>
}

export function ModalBundleItemsSelection({
  handleItemsUpdate,
  itemsInBundle,
}: ModalBundleItemsSelectionProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'items' })
  const { t: tActions } = useTranslation('shared', {
    keyPrefix: 'actions',
  })

  const [selectedItems, setSelectedItems] = useState<
    Map<string, CoreItem & { quantity: number }>
  >(new Map())

  const totalSelectedItems = selectedItems.size
  const totalQuantity = Array.from(selectedItems.values()).reduce(
    (sum, item) => sum + item.quantity,
    0
  )

  const {
    searchTerm,
    groupedItems,
    filteredItems,
    hasMore,
    isLoading,
    isFetching,
    searchInputRef,
    clearSearch,
    handleSearchChange,
    loadMore,
  } = useBundleItemsFilter({ itemsInBundle })

  const handleItemQuantityChange = (
    updatedItem: CoreItem & { quantity: number }
  ) => {
    setSelectedItems((prev) => {
      const newMap = new Map(prev)

      if (updatedItem.quantity > 0) {
        newMap.set(updatedItem.id.toString(), updatedItem)
      } else {
        newMap.delete(updatedItem.id.toString())
      }

      return newMap
    })
  }

  const handleSubmit = () =>
    handleItemsUpdate?.(Array.from(selectedItems.values()))

  return (
    <Modal size='xxl'>
      <Modal.Close />

      <Modal.Title>
        <Flex align='center' gap='sm'>
          <Title order={4} lh={1}>
            {t('new_bundle_modal_title')}
          </Title>

          {!searchTerm && (
            <Badge>
              {t('new_bundle_modal_title_count', {
                count: filteredItems.length,
                replace: { count: filteredItems.length },
              })}
            </Badge>
          )}

          {searchTerm && <Text c='gray.6'>{`> ${searchTerm}`}</Text>}
        </Flex>
      </Modal.Title>

      <Modal.Subtitle>
        <TextInput
          w='100%'
          mt='md'
          ref={searchInputRef}
          placeholder={t('new_bundle_modal_search_placeholder')}
          leftSection={<Icon icon={faSearch} />}
          defaultValue={searchTerm}
          onChange={handleSearchChange}
          radius='sm'
          size='sm'
          rightSection={
            searchTerm ? (
              <Text
                size='sm'
                c='dimmed'
                ml={-20}
                style={{ cursor: 'pointer' }}
                onClick={clearSearch}
              >
                {tActions('clear')}
              </Text>
            ) : null
          }
        />
      </Modal.Subtitle>

      <Modal.Content>
        <LoadingOverlay visible={isLoading} />

        {filteredItems.length > 0 && (
          <Text c='dimmed' size='sm' ml='md' mb='xs'>
            {`${filteredItems.length} ${t('new_bundle_modal_search_result')}`}
          </Text>
        )}

        {filteredItems.length === 0 && !isLoading && !isFetching && (
          <Result variant='empty'>
            <Result.Title>
              {t('new_bundle_modal_results_title', {
                search: searchTerm,
              })}
            </Result.Title>
            <Result.Description>
              {t('new_bundle_modal_results_description')}
            </Result.Description>
          </Result>
        )}

        <Box>
          {Array.from(groupedItems).map(([category, categoryItems]) => (
            <div key={category}>
              <Flex direction='row' mih={40} pl='md' align='center'>
                <Text c='gray.6' fz='sm' fw={500} truncate>
                  {category.split(' > ')[0]}
                </Text>

                {category.split(' > ')[1] && (
                  <>
                    <Icon
                      icon={faCaretRight}
                      mt={1}
                      mr={3}
                      ml={2}
                      color='gray.6'
                      size='xs'
                    />
                    <Text c='gray.6' fz='sm' fw={500} truncate>
                      {category.split(' > ')[1]}
                    </Text>
                  </>
                )}
              </Flex>

              {categoryItems.map((item) => (
                <BundleModalItemRow
                  key={item.internal_id}
                  item={item}
                  onChangeItemQuantity={handleItemQuantityChange}
                />
              ))}
            </div>
          ))}
        </Box>

        {hasMore && (
          <Flex align='center' justify='center' my='md' w='100%'>
            <Button
              size='sm'
              onClick={loadMore}
              leftSection={<Icon icon={faArrowDown} />}
              variant='light'
              loading={isFetching}
            >
              {t('new_bundle_modal_load_more')}
            </Button>
          </Flex>
        )}
      </Modal.Content>

      <Modal.FooterExtra>
        <Flex justify='space-between'>
          <Box />
          {totalSelectedItems === 0 && (
            <Badge color='gray'>{t('new_bundle_modal_no_items')}</Badge>
          )}
          {totalSelectedItems > 0 && (
            <Badge color='teal' variant='light'>
              <Flex align='center' gap='xs'>
                <Text fw={500}>
                  {t('new_bundle_modal_title_count', {
                    count: totalSelectedItems,
                    replace: { count: totalSelectedItems },
                  })}
                </Text>
                <Text>
                  (
                  {t('new_bundle_modal_units', {
                    count: totalQuantity,
                    replace: { count: totalQuantity },
                  })}
                  )
                </Text>
              </Flex>
            </Badge>
          )}
        </Flex>
      </Modal.FooterExtra>

      <Modal.Cancel />
      <Modal.Submit onClick={handleSubmit}>{tActions('save')}</Modal.Submit>
    </Modal>
  )
}
